import type { PixelSettings } from '../../config/types/pixel-settings';
import { bootstrapPixel } from '../bootstrap';
import { installPixelGlobals } from '../global';

export const PRESET_BOOT_KEY = '__beworkePixelPresetBoot__' as const;

export type PixelPresetName =
  | 'hwaffiliate-landing'
  | 'hwaffiliate-checkout'
  | 'buygoods-landing'
  | 'buygoods-checkout'
  | 'internal-checkout';

export type BuygoodsPresetName = PixelPresetName;

export type StartBuygoodsPresetOptions = {
  name: BuygoodsPresetName;
  settings: PixelSettings;
  hopToTop: boolean;
  scriptPattern: RegExp;
};

export type StartPresetDeps = {
  bootstrap: typeof bootstrapPixel;
  install: typeof installPixelGlobals;
};

export type StartPresetResult = 'booted' | 'hopped' | 'skipped';

type WindowWithPresetBoot = Window & {
  [PRESET_BOOT_KEY]?: BuygoodsPresetName;
};

const defaultDeps: StartPresetDeps = {
  bootstrap: bootstrapPixel,
  install: installPixelGlobals,
};

export function resolveAccessibleTopWindow(win: Window = window): Window {
  try {
    const top = win.top;
    if (top && top.document) {
      return top;
    }
  } catch {
    // cross-origin: permanece no frame atual
  }
  return win;
}

export function findPresetScript(
  doc: Document,
  filePattern: RegExp
): HTMLScriptElement | null {
  const current = doc.currentScript;
  if (current instanceof HTMLScriptElement && filePattern.test(current.src)) {
    return current;
  }

  const scripts = doc.querySelectorAll('script[src]');
  for (let i = 0; i < scripts.length; i += 1) {
    const script = scripts[i];
    if (script instanceof HTMLScriptElement && filePattern.test(script.src)) {
      return script;
    }
  }

  return null;
}

export function readPixelIdFromScriptSrc(src: string): string | null {
  try {
    const base =
      typeof window !== 'undefined' ? window.location.href : 'https://local.test/';
    const value = new URL(src, base).searchParams.get('pixelId')?.trim();
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function hasScriptWithSrc(doc: Document, src: string): boolean {
  const scripts = doc.querySelectorAll('script[src]');
  for (let i = 0; i < scripts.length; i += 1) {
    const script = scripts[i];
    if (script instanceof HTMLScriptElement && script.src === src) {
      return true;
    }
  }
  return false;
}

export function injectPresetScript(
  doc: Document,
  src: string,
  presetName: BuygoodsPresetName
): HTMLScriptElement {
  const loader = doc.createElement('script');
  loader.src = src;
  loader.async = false;
  loader.setAttribute('data-beworke-pixel-preset', presetName);
  (doc.head || doc.documentElement).appendChild(loader);
  return loader;
}

/**
 * Sobe o preset no window correto.
 * Checkout injeta o mesmo script em `window.top` (Funnel Pixels / iframe).
 */
export function startBuygoodsPreset(
  options: StartBuygoodsPresetOptions,
  deps: StartPresetDeps = defaultDeps
): StartPresetResult {
  if (typeof window === 'undefined') {
    return 'skipped';
  }

  const target = options.hopToTop ? resolveAccessibleTopWindow(window) : window;

  if (target !== window) {
    const own = findPresetScript(document, options.scriptPattern);
    const src = own?.src;
    if (!src) {
      // eslint-disable-next-line no-console
      console.error(
        `[pixel] preset ${options.name}: script src ausente; não foi possível subir para window.top`
      );
      return 'skipped';
    }
    if (!hasScriptWithSrc(target.document, src)) {
      injectPresetScript(target.document, src, options.name);
    }
    return 'hopped';
  }

  const win = window as WindowWithPresetBoot;
  if (win[PRESET_BOOT_KEY]) {
    return 'skipped';
  }

  win[PRESET_BOOT_KEY] = options.name;
  deps.install();

  void deps.bootstrap(options.settings).catch((error: unknown) => {
    delete win[PRESET_BOOT_KEY];
    // eslint-disable-next-line no-console
    console.error(`[pixel] falha ao iniciar preset ${options.name}`, error);
  });

  return 'booted';
}
